import React from 'react';
import styled from 'styled-components';
import ButtonV2 from 'components/ButtonV2';
import ComponentDemo from '../../components/ComponentDemo';
import CodeBlock from '../../components/CodeBlock';
import PropsTable from '../../components/PropsTable';

const PageHeader = styled.div`
  margin-bottom: 48px;
`;

const Title = styled.h1`
  font-size: 36px;
  font-weight: 700;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 12px 0;
`;

const Subtitle = styled.p`
  font-size: 18px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  margin: 0;
  line-height: 1.6;
`;

const Section = styled.section`
  margin-bottom: 64px;
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 8px 0;
`;

const SectionDescription = styled.p`
  font-size: 15px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  line-height: 1.7;
  margin: 0 0 32px 0;
`;

// Icon components for examples
const SendIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
  </svg>
);

const FilterIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
  </svg>
);

const Buttons = () => {
  const [activeFilter, setActiveFilter] = React.useState('all');

  const buttonProps = [
    {
      name: 'variant',
      type: "'default' | 'pill' | 'icon'",
      default: "'default'",
      required: false,
      description: 'Button style variant. Default is action button, pill for filters, icon for icon-only buttons.',
    },
    {
      name: 'active',
      type: 'boolean',
      default: 'false',
      required: false,
      description: 'Whether the button is in active/selected state. Used with pill buttons for filters.',
    },
    {
      name: 'icon',
      type: 'node',
      required: false,
      description: 'Optional icon element to display. For icon variant, this is the only content.',
    },
    {
      name: 'children',
      type: 'node',
      required: false,
      description: 'Button text/content. Not used with icon variant.',
    },
    {
      name: 'onClick',
      type: 'function',
      required: false,
      description: 'Click handler function.',
    },
    {
      name: 'disabled',
      type: 'boolean',
      default: 'false',
      required: false,
      description: 'Whether the button is disabled.',
    },
    {
      name: 'title',
      type: 'string',
      required: false,
      description: 'Tooltip text. Especially important for icon-only buttons.',
    },
    {
      name: 'type',
      type: 'string',
      default: "'button'",
      required: false,
      description: 'Button type attribute (button, submit, reset).',
    },
  ];

  return (
    <div>
      <PageHeader>
        <Title>Buttons</Title>
        <Subtitle>
          ButtonV2 is our unified button component with three variants: default
          action buttons, pill-shaped filter buttons, and icon-only buttons. All
          variants support light and dark themes.
        </Subtitle>
      </PageHeader>

      <Section>
        <SectionTitle>Default Variant</SectionTitle>
        <SectionDescription>
          The default variant is used for primary actions throughout the application.
          It features a distinctive orange background (#FA4616) with white text and icon.
        </SectionDescription>

        <ComponentDemo label="Default Button">
          <ButtonV2 onClick={() => console.log('Clicked')}>
            Save Changes
          </ButtonV2>
        </ComponentDemo>

        <ComponentDemo label="Default Button with Icon">
          <ButtonV2 icon={<SendIcon />} onClick={() => console.log('Sent')}>
            Send Campaign
          </ButtonV2>
        </ComponentDemo>

        <ComponentDemo label="Disabled State">
          <ButtonV2 disabled>
            Disabled Button
          </ButtonV2>
        </ComponentDemo>

        <CodeBlock>
{`import ButtonV2 from 'components/ButtonV2';

// Basic default button
<ButtonV2 onClick={handleClick}>
  Save Changes
</ButtonV2>

// With icon
<ButtonV2 icon={<SendIcon />} onClick={handleSend}>
  Send Campaign
</ButtonV2>

// Disabled
<ButtonV2 disabled>
  Disabled Button
</ButtonV2>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Pill Variant</SectionTitle>
        <SectionDescription>
          Pill buttons are used for filters and toggles. They have a rounded shape
          and change appearance when active. Used extensively in the Customers page
          for segment filters.
        </SectionDescription>

        <ComponentDemo label="Pill Buttons (Interactive)" gap="12px" wrap>
          <ButtonV2
            variant="pill"
            active={activeFilter === 'all'}
            onClick={() => setActiveFilter('all')}
          >
            All Customers
          </ButtonV2>
          <ButtonV2
            variant="pill"
            active={activeFilter === 'super'}
            onClick={() => setActiveFilter('super')}
          >
            Super Fans
          </ButtonV2>
          <ButtonV2
            variant="pill"
            active={activeFilter === 'regular'}
            onClick={() => setActiveFilter('regular')}
          >
            Regular Fans
          </ButtonV2>
          <ButtonV2
            variant="pill"
            active={activeFilter === 'casual'}
            onClick={() => setActiveFilter('casual')}
          >
            Casual Fans
          </ButtonV2>
          <ButtonV2
            variant="pill"
            active={activeFilter === 'first'}
            onClick={() => setActiveFilter('first')}
          >
            First Timers
          </ButtonV2>
        </ComponentDemo>

        <CodeBlock>
{`import ButtonV2 from 'components/ButtonV2';

const [activeFilter, setActiveFilter] = useState('all');

// Pill buttons for filters
<ButtonV2
  variant="pill"
  active={activeFilter === 'all'}
  onClick={() => setActiveFilter('all')}
>
  All Customers
</ButtonV2>

<ButtonV2
  variant="pill"
  active={activeFilter === 'super'}
  onClick={() => setActiveFilter('super')}
>
  Super Fans
</ButtonV2>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Icon Variant</SectionTitle>
        <SectionDescription>
          Icon-only buttons are compact and used when space is limited or when the
          icon clearly communicates the action. Always include a title prop for
          accessibility.
        </SectionDescription>

        <ComponentDemo label="Icon Buttons" gap="12px">
          <ButtonV2
            variant="icon"
            icon={<FilterIcon />}
            onClick={() => console.log('Filter')}
            title="Filter"
          />
          <ButtonV2
            variant="icon"
            icon={<SendIcon />}
            onClick={() => console.log('Send')}
            title="Send"
          />
          <ButtonV2
            variant="icon"
            icon={<FilterIcon />}
            disabled
            title="Disabled"
          />
        </ComponentDemo>

        <CodeBlock>
{`import ButtonV2 from 'components/ButtonV2';

// Icon-only button
<ButtonV2
  variant="icon"
  icon={<FilterIcon />}
  onClick={handleFilter}
  title="Filter"  // Important for accessibility
/>

// Active state
<ButtonV2
  variant="icon"
  icon={<FilterIcon />}
  active
  onClick={handleFilter}
  title="Filter Active"
/>

// Disabled
<ButtonV2
  variant="icon"
  icon={<FilterIcon />}
  disabled
  title="Filter Disabled"
/>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Button States</SectionTitle>
        <SectionDescription>
          All button variants support active, hover, focus, and disabled states with
          appropriate visual feedback.
        </SectionDescription>

        <ComponentDemo label="State Examples" direction="column" gap="16px" align="flex-start">
          <ButtonV2>Normal State</ButtonV2>
          <ButtonV2 active>Active State</ButtonV2>
          <ButtonV2 disabled>Disabled State</ButtonV2>
        </ComponentDemo>

        <CodeBlock>
{`// States are managed through props
<ButtonV2>Normal</ButtonV2>
<ButtonV2 active>Active</ButtonV2>
<ButtonV2 disabled>Disabled</ButtonV2>

// Hover and focus states are automatic`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Component Props</SectionTitle>
        <PropsTable props={buttonProps} />
      </Section>

      <Section>
        <SectionTitle>Usage Guidelines</SectionTitle>
        <SectionDescription>
          Best practices for using buttons in the application.
        </SectionDescription>

        <ComponentDemo direction="column" gap="16px">
          <div style={{ fontSize: '14px', lineHeight: '1.8', color: 'inherit' }}>
            {/* eslint-disable-next-line jsx-a11y/accessible-emoji */}
            <strong>✅ Do:</strong>
            <br />
            • Use default variant for primary actions (Save, Send, Create)
            <br />
            • Use pill variant for filters and toggles
            <br />
            • Use icon variant when space is limited
            <br />
            • Include title prop on icon buttons for accessibility
            <br />
            • Use clear, action-oriented button text
            <br />
            • Disable buttons when actions are unavailable
            <br />
            <br />
            {/* eslint-disable-next-line jsx-a11y/accessible-emoji */}
            <strong>❌ Don't:</strong>
            <br />
            • Don't use multiple button variants for the same action type
            <br />
            • Don't create icon buttons without title tooltips
            <br />
            • Don't use pill buttons for primary actions
            <br />
            • Don't put too much text in pill buttons
            <br />• Don't forget to handle disabled states
          </div>
        </ComponentDemo>
      </Section>

      <Section>
        <SectionTitle>Real-World Examples</SectionTitle>
        <SectionDescription>
          How buttons are used in actual application pages.
        </SectionDescription>

        <CodeBlock>
{`// From Customers page - Filter buttons
<ButtonV2
  variant="pill"
  active={segment === 'all'}
  onClick={() => setSegment('all')}
>
  All Customers ({totalCount})
</ButtonV2>

// From Message Composer - Send action
<ButtonV2
  icon={<SendIcon />}
  onClick={handleSendMessage}
  disabled={!message.trim()}
>
  Send Message
</ButtonV2>

// From Table - Icon button for actions
<ButtonV2
  variant="icon"
  icon={<MoreIcon />}
  onClick={handleShowMenu}
  title="More actions"
/>`}
        </CodeBlock>
      </Section>
    </div>
  );
};

export default Buttons;